import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth.js';
import { useTheme } from '../lib/theme.js';
import { useEntries } from '../lib/useEntries.js';
import { adminApi } from '../lib/api.js';
import { getPushStatus } from '../lib/push.js';
import {
  TREND_WINDOW,
  estimateEnergy,
  fromISODay,
  profileComplete,
  toISODay,
  type BodyProfile,
  type Goal,
} from '../lib/stats.js';
import * as fmt from '../lib/format.js';
import { APP_VERSION } from '../lib/version.js';
import { CalendarCard } from './CalendarCard.js';
import { StatsRow } from './StatsRow.js';
import { TrendCard } from './TrendCard.js';
import { WeeksCard } from './WeeksCard.js';
import { TimelineCard } from './TimelineCard.js';
import { DayPanel } from './DayPanel.js';
import { AdminPanel } from './AdminPanel.js';
import { InfoModal } from './InfoModal.js';
import { MethodModal } from './MethodModal.js';
import { SettingsPanel } from './SettingsPanel.js';
import { ShareModal } from './ShareModal.js';

/** Rappel de profil masqué : confort par navigateur, pas une donnée de compte. */
const DISMISS_KEY = 'ephemeris.profile-nudge-dismissed';

const readDismissed = (): boolean => {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

export function Dashboard() {
  const { user, updateProfile } = useAuth();
  const [theme, setTheme] = useTheme();
  const entries = useEntries(Boolean(user));

  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [panelOpen, setPanelOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [methodOpen, setMethodOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Préférence d'affichage, pas de profil : elle ne mérite pas un aller-retour.
  const [trendWindow, setTrendWindow] = useState<number>(TREND_WINDOW);
  const [nudgeDismissed, setNudgeDismissed] = useState(readDismissed);
  const [adminOpen, setAdminOpen] = useState(false);
  const [pending, setPending] = useState(0);

  // À chaque ouverture, l'abonnement push de cet appareil est renvoyé au
  // serveur : s'il l'avait perdu, les rappels reprennent sans que personne
  // n'ait à passer par les réglages. Silencieux par construction.
  useEffect(() => {
    void getPushStatus().catch(() => undefined);
  }, []);

  // Relevé au chargement puis après chaque décision : pas de sondage. C'est la
  // notification push qui prévient entre-temps.
  useEffect(() => {
    if (!user?.isAdmin) return;
    void adminApi
      .requests()
      .then((result) => setPending(result.requests.length))
      .catch(() => undefined);
  }, [user?.isAdmin]);

  const profile = useMemo<BodyProfile>(
    () => ({
      sex: user?.sex ?? null,
      birthYear: user?.birthYear ?? null,
      heightCm: user?.heightCm ?? null,
      startWeightKg: user?.startWeight ?? null,
    }),
    [user?.sex, user?.birthYear, user?.heightCm, user?.startWeight],
  );

  // La sélection par défaut est aujourd'hui — et non le dernier jour de la
  // série de saisie, qui est demain : on ouvre sur la journée en cours, pas sur
  // celle qu'on prépare. Elle ne peut être posée qu'une fois les données
  // arrivées.
  useEffect(() => {
    if (selectedDay || entries.series.length === 0) return;
    setSelectedDay(entries.series[entries.todayIndex]?.day ?? null);
  }, [entries.series, entries.todayIndex, selectedDay]);

  const selectedIndex = useMemo(
    () => (selectedDay ? entries.indexOf(selectedDay) : -1),
    [entries, selectedDay],
  );

  // Journées effectivement renseignées : l'import s'en sert pour annoncer
  // combien il va en remplacer — la seule partie irréversible de l'opération —
  // et l'export pour ne sortir que ce qui existe.
  const recorded = useMemo(
    () =>
      entries.editable
        .filter(
          (row) =>
            row.weight != null || row.steps != null || row.kcal != null || row.gym != null,
        )
        .map(({ day, weight, steps, kcal, gym }) => ({ day, weight, steps, kcal, gym })),
    [entries.editable],
  );

  const existingDays = useMemo(() => new Set(recorded.map((row) => row.day)), [recorded]);

  const today = entries.series[entries.todayIndex] ?? null;
  const goal = (user?.goal ?? 'loss') as Goal;

  function select(day: string, openPanel = false) {
    setSelectedDay(day);
    const date = fromISODay(day);
    setMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    if (openPanel) setPanelOpen(true);
  }

  function dismissNudge() {
    setNudgeDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Navigation privée ou stockage bloqué : le rappel reviendra, tant pis.
    }
  }

  if (entries.loading) {
    return (
      <main className="dash">
        <p className="muted">Chargement des données…</p>
      </main>
    );
  }

  // Le rappel ne s'affiche que s'il dit vrai : tant que le bilan énergétique
  // n'a pas assez de saisies, la morphologie porte réellement l'estimation.
  // Dès qu'il prend le relais, elle ne sert plus qu'au repli et à la
  // comparaison, et insister deviendrait du harcèlement gratuit.
  const modelled =
    estimateEnergy(entries.series, entries.series.length - 1, profile).source === 'predicted';
  const showNudge =
    Boolean(user) && modelled && !profileComplete(profile) && !nudgeDismissed;

  return (
    <>
      <header className="appbar">
        <div className="appbar__left">
          <div className="brand">
            <span>Ephemeris</span>
          </div>
          <div className="appbar__sep" aria-hidden="true" />
          <div className="appbar__date">{today ? fmt.fullDate(today.date) : ''}</div>
        </div>

        <div className="appbar__right">
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => select(toISODay(new Date()), true)}
          >
            Saisir aujourd’hui
          </button>

          {/* Le geste du soir : les pas, les calories et la séance d'une ligne
              sont ceux de la veille, donc ceux d'aujourd'hui se notent sur la
              journée de demain, où ils attendront la pesée du matin. Le bouton
              ne vise que demain ; les quatorze jours suivants s'ouvrent depuis
              le calendrier, pour qui saisit un plan de repas d'un coup. */}
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            onClick={() => select(entries.tomorrow, true)}
            title="Noter dès ce soir les pas, les calories et la séance d’aujourd’hui. Le calendrier ouvre les quinze jours à venir."
          >
            Préparer demain
          </button>

          {user?.isAdmin ? (
            <button
              type="button"
              className="iconbtn"
              onClick={() => setAdminOpen(true)}
              aria-label={
                pending === 0
                  ? 'Demandes de compte : aucune en attente'
                  : `Demandes de compte : ${pending} en attente`
              }
            >
              <span aria-hidden="true">🔔</span>
              {pending > 0 ? <span className="bell__badge">{pending}</span> : null}
            </button>
          ) : null}

          <button
            type="button"
            className="iconbtn"
            onClick={() => setShareOpen(true)}
            aria-label="Partager ma semaine"
            title="Partager ma semaine en image"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M8 10V2M5 5l3-3 3 3" />
              <path d="M3 8.5V13h10V8.5" />
            </svg>
          </button>

          <div className="segmented" role="group" aria-label="Thème">
            <button type="button" aria-pressed={theme === 'light'} onClick={() => setTheme('light')}>
              Clair
            </button>
            <button type="button" aria-pressed={theme === 'dark'} onClick={() => setTheme('dark')}>
              Sombre
            </button>
          </div>

          <button type="button" className="avatar" onClick={() => setSettingsOpen(true)}>
            <span className="avatar__initials">
              {fmt.initials(user?.displayName ?? null, user?.email ?? '?')}
            </span>
            <span>{user?.displayName ?? user?.email}</span>
          </button>
        </div>
      </header>

      <main className="dash">
        {entries.error ? (
          <p className="error" role="alert">
            {entries.error}
          </p>
        ) : null}

        {showNudge ? (
          <div className="nudge" role="status">
            <div>
              <b>Profil incomplet.</b> Sans taille ni année de naissance, le maintien calorique
              repose sur un forfait au kilo, à ±15 % près. Deux champs suffisent à le remplacer par
              une estimation ancrée — et le bilan énergétique prendra le relais dès quatorze jours
              d’apports saisis.
            </div>
            <div className="nudge__actions">
              <button type="button" className="btn btn--sm" onClick={() => setSettingsOpen(true)}>
                Compléter
              </button>
              <button type="button" className="btn btn--sm btn--ghost" onClick={dismissNudge}>
                Plus tard
              </button>
            </div>
          </div>
        ) : null}

        <div className="dash__columns">
          <div className="dash__col">
            <CalendarCard
              series={entries.editable}
              maxDay={entries.maxDay}
              month={month}
              onMonthChange={setMonth}
              selectedDay={selectedDay}
              onSelect={(day) => select(day, true)}
            />
            <StatsRow series={entries.series} profile={profile} />
          </div>

          <div className="dash__col">
            <TrendCard
              series={entries.series}
              // La tendance s'arrête à aujourd'hui : demain n'a pas de pesée,
              // et une fenêtre décalée d'un jour vide ne dirait rien de plus.
              selectedIndex={Math.min(selectedIndex, entries.todayIndex)}
              goal={goal}
              profile={profile}
              window={trendWindow}
              onWindowChange={setTrendWindow}
              onGoalChange={(next) => void updateProfile({ goal: next })}
            />
            <WeeksCard series={entries.series} goal={goal} />
          </div>
        </div>

        <TimelineCard
          series={entries.series}
          selectedDay={selectedDay}
          goal={goal}
          profile={profile}
          onSelect={(day) => select(day)}
          onOpen={(day) => select(day, true)}
          onOpenInfo={() => setInfoOpen(true)}
        />

        <footer className="dash__footer">
          <span>
            Ephemeris <span className="version">v{APP_VERSION}</span>
          </span>
          <button type="button" className="btn--ghost btn btn--sm" onClick={() => setMethodOpen(true)}>
            Méthode et sources
          </button>
          <button type="button" className="btn--ghost btn btn--sm" onClick={() => setSettingsOpen(true)}>
            Réglages
          </button>
        </footer>
      </main>

      {panelOpen && selectedIndex >= 0 ? (
        <DayPanel
          series={entries.editable}
          profile={profile}
          index={selectedIndex}
          todayIndex={entries.todayIndex}
          onNavigate={(index) => {
            const row = entries.editable[index];
            if (row) select(row.day);
          }}
          onSave={(day, values) => void entries.save(day, values)}
          onClear={(day) => void entries.remove(day)}
          onClose={() => setPanelOpen(false)}
        />
      ) : null}

      {adminOpen ? (
        <AdminPanel onClose={() => setAdminOpen(false)} onChange={setPending} />
      ) : null}
      {infoOpen ? <InfoModal onClose={() => setInfoOpen(false)} /> : null}
      {methodOpen ? <MethodModal onClose={() => setMethodOpen(false)} /> : null}
      {shareOpen ? (
        <ShareModal
          series={entries.series}
          profile={profile}
          goal={goal}
          trendWindow={trendWindow}
          theme={theme}
          onClose={() => setShareOpen(false)}
        />
      ) : null}
      {settingsOpen ? (
        <SettingsPanel
          onClose={() => setSettingsOpen(false)}
          existingDays={existingDays}
          recorded={recorded}
          onImported={() => void entries.reload()}
        />
      ) : null}
    </>
  );
}
